import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

interface StatusOption {
  value: string;
  label: string;
  color: string;
}

interface StatusChangeButtonProps {
  currentStatus: string;
  onStatusChange: (newStatus: string) => void;
  disabled?: boolean;
}

const statusConfig: Record<string, { label: string; color: string }> = {
  'attente': { label: 'En attente', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  'confirme': { label: 'Confirmé', color: 'bg-green-100 text-green-700 border-green-200' },
  'realise': { label: 'Réalisé', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  'annule': { label: 'Annulé', color: 'bg-red-100 text-red-700 border-red-200' },
  'non_honore': { label: 'Non honoré', color: 'bg-orange-100 text-orange-700 border-orange-200' }
};

export default function StatusChangeButton({ currentStatus, onStatusChange, disabled }: StatusChangeButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentConfig = statusConfig[currentStatus] || { label: currentStatus, color: 'bg-gray-100 text-gray-800 border-gray-200' };

  // Fermer le dropdown si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Options disponibles (toutes sauf le statut actuel)
  const availableOptions: StatusOption[] = Object.entries(statusConfig)
    .filter(([status]) => status !== currentStatus)
    .map(([value, config]) => ({
      value,
      label: config.label,
      color: config.color
    }));

  const handleStatusSelect = (newStatus: string) => {
    onStatusChange(newStatus);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`px-2 py-1 text-xs font-medium rounded-full border ${currentConfig.color} flex items-center space-x-1 hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed`}
        title="Changer le statut"
      >
        <span>{currentConfig.label}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 min-w-40">
          {availableOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handleStatusSelect(option.value)}
              disabled={disabled}
              className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 first:rounded-t-md last:rounded-b-md transition-colors"
            >
              <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full border ${option.color}`}>
                {option.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}